import type { AudioEntry } from './types';

export function normalizeAudioItems(
  response: Record<string, unknown>,
  _page: number,
): AudioEntry[] {
  const raw = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response?.data)
      ? response.data
      : [];

  return (raw as Record<string, unknown>[])
    .map((item) => {
      const audioUrl = typeof item?.url === 'string' ? item.url : '';
      if (!audioUrl) return null;

      const languageCode = extractLanguageCode(item);

      return {
        id: String(item?.id ?? item?._id ?? item?.audioId ?? ''),
        word: String(item?.phraseLocal ?? item?.word ?? item?.text ?? ''),
        audioUrl,
        transcription: String(item?.transcription ?? ''),
        validated: Boolean(item?.validated),
        langFr: String(item?.phraseFr ?? item?.langFr ?? ''),
        ...(languageCode ? { languageCode } : {}),
        hasRejectedAttempt: Boolean(item?.hasRejectedAttempt),
        rejectionReasons: Array.isArray(item?.rejectionReasons)
          ? (item.rejectionReasons as unknown[]).map((reason) => String(reason))
          : [],
        isInitialAnswer: item?.source === 'initialAudio',
      };
    })
    .filter((entry): entry is AudioEntry => entry !== null);
}

/**
 * API versions do not expose the audio language under one single property.
 * Keep the normalization at the boundary so the rest of the feature works
 * with one stable field and can protect the session from mixed datasets.
 */
function extractLanguageCode(item: Record<string, unknown>): string | undefined {
  const candidates: unknown[] = [
    item.languageCode,
    item.lang,
    item.language,
    item.locale,
    (item.metadata as Record<string, unknown> | undefined)?.languageCode,
    (item.metadata as Record<string, unknown> | undefined)?.lang,
    (item.metadata as Record<string, unknown> | undefined)?.language,
    (item.audio as Record<string, unknown> | undefined)?.languageCode,
    (item.audio as Record<string, unknown> | undefined)?.lang,
    (item.audio as Record<string, unknown> | undefined)?.language,
  ];

  for (const candidate of candidates) {
    const value = typeof candidate === 'object' && candidate !== null
      ? (candidate as Record<string, unknown>).code ?? (candidate as Record<string, unknown>).id
      : candidate;
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  return undefined;
}

export function extractTotal(response: Record<string, unknown>): number | null {
  if (typeof response?.total === 'number') return response.total;
  if (typeof response?.count === 'number') return response.count;
  if (typeof (response?.data as Record<string, unknown>)?.total === 'number')
    return (response.data as Record<string, unknown>).total as number;
  return null;
}

export function normalizeLanguageKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

export function mapLanguageToCode(
  value: string,
  languages: Array<{ code: string; name?: string; label?: string; frenchName?: string }>,
): string {
  const normalized = normalizeLanguageKey(value);
  const match = languages.find((l) => {
    const candidates = [l.code, l.name, l.label, l.frenchName].filter(Boolean);
    return candidates.some((c) => normalizeLanguageKey(c!) === normalized);
  });
  if (match?.code) return match.code;

  const legacy: Record<string, string> = { francais: 'fr', lamba: 'loso' };
  return legacy[normalized] || normalized;
}

export function filterAudioItemsByLanguage(
  items: AudioEntry[],
  expectedLanguage: string,
  languages: Array<{ code: string; name?: string; label?: string; frenchName?: string }>,
): AudioEntry[] {
  const taggedItems = items.filter((item) => item.languageCode);
  if (taggedItems.length === 0) return items;

  const expectedCode = mapLanguageToCode(expectedLanguage, languages);
  const acceptedKeys = new Set<string>([normalizeLanguageKey(expectedCode)]);

  languages.forEach((language) => {
    if (normalizeLanguageKey(language.code) !== normalizeLanguageKey(expectedCode)) return;
    [language.code, language.name, language.label, language.frenchName]
      .filter(Boolean)
      .forEach((value) => acceptedKeys.add(normalizeLanguageKey(value!)));
  });

  return items.filter((item) => {
    if (!item.languageCode) return true;
    return acceptedKeys.has(normalizeLanguageKey(item.languageCode));
  });
}

export function bufferToBlobUrl(buf: unknown): string | null {
  try {
    if (!buf) return null;
    const arr =
      buf instanceof ArrayBuffer
        ? new Uint8Array(buf)
        : (buf as Record<string, unknown>)?.data && Array.isArray((buf as Record<string, unknown>).data)
          ? new Uint8Array((buf as Record<string, unknown>).data as number[])
          : null;
    if (!arr) return null;
    return URL.createObjectURL(new Blob([arr], { type: 'audio/wav' }));
  } catch {
    return null;
  }
}
